export class HeroArcs {
	constructor(canvas) {
		this.canvas = canvas;
		this.ctx = canvas.getContext("2d");
		this.container = canvas.closest(".intro");

		if (!this.container) {
			console.warn("HeroArcs: .intro container not found");
			return;
		}

		// Get stroke color from CSS variable
		const style = getComputedStyle(document.documentElement);
		this.strokeColor =
			style.getPropertyValue("--green-dark").trim() || "#FF5C00";
		this.strokeWidth = 1;

		this.arcs = [];
		this.animationDuration = 800;
		this.startTime = null;
		this.animationId = null;

		// Store bound handlers for cleanup
		this.handleResizeBound = this.handleResize.bind(this);
		this.animateBound = this.animate.bind(this);

		this.init();
	}

	getDimensions() {
		const rect = this.container.getBoundingClientRect();
		return { width: rect.width, height: rect.height };
	}

	init() {
		this.setupCanvas();
		this.createArcs();
		window.addEventListener("resize", this.handleResizeBound);
		this.animationId = requestAnimationFrame(this.animateBound);
	}

	setupCanvas() {
		const { width, height } = this.getDimensions();
		const dpr = window.devicePixelRatio || 1;

		this.canvas.style.width = `${width}px`;
		this.canvas.style.height = `${height}px`;
		this.canvas.width = width * dpr;
		this.canvas.height = height * dpr;

		this.ctx.scale(dpr, dpr);
		this.width = width;
		this.height = height;
	}

	createArcs() {
		const { width, height } = this;
		this.arcs = [];

		// Single ripple source from top-left - large arcs spanning whole hero
		const diagonal = Math.sqrt(width * width + height * height);
		const source = {
			cx: -width * 0.05,
			cy: -height * 0.1,
			count: 14,
			startRadius: diagonal * 0.08,
			spacing: diagonal * 0.08,
			startAngle: 0,
			endAngle: 0.5,
		};

		// Generate concentric arcs
		for (let i = 0; i < source.count; i++) {
			this.arcs.push({
				cx: source.cx,
				cy: source.cy,
				radius: source.startRadius + i * source.spacing,
				startAngle: source.startAngle,
				endAngle: source.endAngle,
				delay: i * 0.02,
			});
		}
	}

	animate(timestamp) {
		if (!this.startTime) this.startTime = timestamp;

		const elapsed = timestamp - this.startTime;
		const progress = Math.min(elapsed / this.animationDuration, 1);

		this.draw(progress);

		if (progress < 1) {
			this.animationId = requestAnimationFrame(this.animateBound);
		}
	}

	draw(progress) {
		this.ctx.clearRect(0, 0, this.width, this.height);
		this.ctx.strokeStyle = this.strokeColor;
		this.ctx.lineWidth = this.strokeWidth;

		for (const arc of this.arcs) {
			// Staggered progress per arc
			const arcProgress = Math.max(
				0,
				Math.min(1, (progress - arc.delay) / (1 - arc.delay * 0.5))
			);
			if (arcProgress <= 0) continue;

			// Ease out
			const eased = 1 - Math.pow(1 - arcProgress, 3);

			// Fade + subtle expand (90% to 100%)
			this.ctx.globalAlpha = eased;
			const currentRadius = arc.radius * (0.9 + 0.1 * eased);

			this.ctx.beginPath();
			this.ctx.arc(
				arc.cx,
				arc.cy,
				currentRadius,
				arc.startAngle * Math.PI,
				arc.endAngle * Math.PI
			);
			this.ctx.stroke();
		}

		this.ctx.globalAlpha = 1;
	}

	handleResize() {
		this.setupCanvas();
		this.createArcs();
		// Redraw at final state
		this.draw(1);
	}

	destroy() {
		window.removeEventListener("resize", this.handleResizeBound);
		if (this.animationId) {
			cancelAnimationFrame(this.animationId);
		}
	}
}
